/* eslint-disable */
import { useState, useMemo } from "react";
import { LOGO_B64, usd, pct, C, styles } from "../ui/tokens";
import {
  SectionHeader, StatCard, HeroBanner, MetricLine, PillTabs, SliderField, Callout,
  ProjectionTable, DualAxisChart, AccountForm, AccountEditForm,
} from "../ui/primitives";
import { EfficientFrontierChart } from "../ui/EfficientFrontierChart";
import { getDistributionPeriod } from "../engine/tables";
import { HIST_FIRST, HIST_LAST } from "../engine/hist";
import { calculateRMD, calculateProjections, scenarioBalance, inheritedAccounts, ageAtYearEnd } from "../engine/rmd";
import { runScenario, summarize } from "../engine/scenario";
import { downloadEML, downloadDOCX } from "../reports/clientReports";
import { downloadProspectEML, downloadProspectDOCX } from "../reports/prospectReports";

// ─── Per-person scenario assumptions (saved on the record) ────────────────────
export const DEFAULT_SCENARIO = {
  returnMode: "assumed",              // "assumed" | "hist"
  equityRate: 0.08,                   // assumed mode: equity sleeve RoR
  bondRate: 0.04,                     // assumed mode: bond sleeve RoR
  bondPct: 0.40,                      // assumed mode: bond sleeve % of IRA before purchase
  dividendYield: 0.02,                // assumed mode: FIA credits equity − yield (price index proxy)
  returnRate: 0.05,                   // legacy (derived now) — kept for saved records
  postSpiaReturnRate: 0.05,           // legacy (derived now)
  allocationPct: 0.75,                // SPIA allocation, % of RMD
  spiaTerm: 10,
  spiaRate: 0.039,
  arbitrageRate: 0,
  rebalanceAges: "",                  // comma-separated ages; holistic re-run of the allocation rule at each
  allocs: { spx: 60, djia: 0, ndx: 0, eafe: 20, agg: 20 },
  yearBegin: 2000, yearEnd: 2009, flipped: false,
  fia: { allocationPct: 0.30, term: 5, participation: 1.0, cap: 0, rebalance: false, terminateYear: 0 },
};
const mergeScenario = (s) => ({ ...DEFAULT_SCENARIO, ...(s || {}), allocs: { ...DEFAULT_SCENARIO.allocs, ...(s?.allocs || {}) }, fia: { ...DEFAULT_SCENARIO.fia, ...(s?.fia || {}) } });

const AMBER = "#d97706";
const accountType = (a) => {
  const ann = a.is_annuity ? (a.annuity_type === "fia" ? "FIA" : a.annuity_type === "deferred" ? "Deferred Annuity" : "Income Annuity") : null;
  if (a.is_inherited_ira) return ann ? `Inherited ${ann}` : "Inherited IRA";
  return ann || "Traditional IRA";
};

// ═════════════════════════════════════════════════════════════════════════════
export function PersonWorkspace({ clients, setClients, settings }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [editAccountId, setEditAccountId] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", dob: "", contact_email: "", contact_address: "", is_prospect: false });
  const [subTab, setSubTab] = useState("rmd");

  const logoSrc = settings?.logoDataUrl || `data:image/jpeg;base64,${LOGO_B64}`;
  const sortedKeys = Object.keys(clients).sort();
  const client = selectedKey ? clients[selectedKey] : null;
  const setClient = (updater) => setClients(c => ({ ...c, [selectedKey]: updater(c[selectedKey]) }));

  const savePerson = () => {
    if (!form.first_name || !form.last_name || !form.dob) return;
    const key = `${form.last_name}, ${form.first_name}`;
    if (showEdit && client) {
      const updated = { ...client, ...form };
      const next = { ...clients }; if (key !== selectedKey) delete next[selectedKey]; next[key] = updated;
      setClients(next);
    } else {
      setClients(c => ({ ...c, [key]: { ...form, accounts: [], scenario: { ...DEFAULT_SCENARIO } } }));
    }
    setSelectedKey(key); setShowAdd(false); setShowEdit(false);
  };
  const openEdit = () => { setForm({ first_name: client.first_name || "", last_name: client.last_name || "", dob: client.dob || "", contact_email: client.contact_email || "", contact_address: client.contact_address || "", is_prospect: !!client.is_prospect }); setShowEdit(true); setShowAdd(false); };
  const openAdd = () => { setForm({ first_name: "", last_name: "", dob: "", contact_email: "", contact_address: "", is_prospect: false }); setShowAdd(v => !v); setShowEdit(false); };
  const deletePerson = () => { const next = { ...clients }; delete next[selectedKey]; setClients(next); setSelectedKey(null); setShowDelete(false); };

  const addAccount = (acct) => { setClient(c => ({ ...c, accounts: [...(c.accounts || []), acct] })); setShowAddAccount(false); };
  const removeAccount = (id) => setClient(c => ({ ...c, accounts: (c.accounts || []).filter(a => a.id !== id) }));
  const updateAccount = (u) => { setClient(c => ({ ...c, accounts: (c.accounts || []).map(a => a.id === u.id ? u : a) })); setEditAccountId(null); };

  const scenario = mergeScenario(client?.scenario);
  const setScenario = (patch) => setClient(c => ({ ...c, scenario: { ...mergeScenario(c.scenario), ...patch } }));

  const personForm = (title, onSave, onCancel) => (
    <div style={{ background: "#f0f4fa", borderRadius: 12, padding: 20, marginTop: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div><label style={styles.label}>First Name</label><input style={styles.input} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} /></div>
        <div><label style={styles.label}>Last Name</label><input style={styles.input} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} /></div>
        <div><label style={styles.label}>Date of Birth</label><input style={styles.input} type="date" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} /></div>
        <div><label style={styles.label}>Status</label>
          <select style={styles.input} value={form.is_prospect ? "prospect" : "client"} onChange={e => setForm(f => ({ ...f, is_prospect: e.target.value === "prospect" }))}>
            <option value="client">Client</option><option value="prospect">Prospect</option>
          </select>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div><label style={styles.label}>Contact Email</label><input style={styles.input} type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} /></div>
        <div><label style={styles.label}>Contact Address</label><input style={styles.input} value={form.contact_address} onChange={e => setForm(f => ({ ...f, contact_address: e.target.value }))} /></div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button style={styles.btnPrimary} onClick={onSave}>Save</button>
        <button style={styles.btnSecondary} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div>
      {/* ── Selector ── */}
      <div style={styles.card}>
        <SectionHeader icon="👥" title="Clients & Prospects" subtitle="Select a person — accounts below, reports in the tabs" />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={styles.label}>Select</label>
            <select style={styles.input} value={selectedKey || ""} onChange={e => { setSelectedKey(e.target.value || null); setShowEdit(false); setEditAccountId(null); }}>
              <option value="">— Choose a client or prospect —</option>
              {sortedKeys.map(k => <option key={k} value={k}>{k}{clients[k].is_prospect ? "  (prospect)" : ""}</option>)}
            </select>
          </div>
          <button style={styles.btnPrimary} onClick={openAdd}>+ Add</button>
          {client && <button style={styles.btnSecondary} onClick={openEdit}>✏️ Edit</button>}
          {client && <button style={styles.btnDanger} onClick={() => setShowDelete(true)}>Delete</button>}
        </div>
        {showDelete && (
          <div style={{ marginTop: 12, background: "#fff0f3", borderRadius: 10, padding: 16, border: "1px solid #fecdd3" }}>
            <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600 }}>Delete {selectedKey}?</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.btnDanger} onClick={deletePerson}>Yes, Delete</button>
              <button style={styles.btnSecondary} onClick={() => setShowDelete(false)}>Cancel</button>
            </div>
          </div>
        )}
        {showAdd && personForm("New Client / Prospect", savePerson, () => setShowAdd(false))}
        {showEdit && client && personForm("Edit", savePerson, () => setShowEdit(false))}
      </div>

      {client && (
        <>
          {/* ── Person header ── */}
          <div style={styles.card}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: "-0.02em" }}>{client.first_name} {client.last_name}</div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: client.is_prospect ? "#fef3c7" : "#dcfce7", color: client.is_prospect ? "#92400e" : "#166534" }}>{client.is_prospect ? "PROSPECT" : "CLIENT"}</span>
            </div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 16, fontSize: 13, color: C.textSec }}>
              <div>📅 DOB: <span style={{ color: C.text, fontWeight: 500 }}>{client.dob}</span></div>
              {client.contact_email && <div>✉️ <a href={`mailto:${client.contact_email}`} style={{ color: C.blue }}>{client.contact_email}</a></div>}
              {client.contact_address && <div>📍 <span style={{ color: C.text }}>{client.contact_address}</span></div>}
            </div>
            {(() => {
              const r = calculateRMD(client);
              const ageYE = ageAtYearEnd(client.dob);
              return (
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <StatCard label="Age at Year End" value={ageYE} />
                  <StatCard label="RMD Required Age" value={r.rmdAge} />
                  <StatCard label="Distribution Period" value={r.divisor?.toFixed(1) || "—"} sublabel="Uniform Lifetime Table" />
                  <StatCard label="Owned IRA (scenario base)" value={usd(scenarioBalance(client))} sublabel="excl. inherited & annuities" />
                  <StatCard label="Accounts" value={client.accounts?.length || 0} sublabel={`${inheritedAccounts(client.accounts).length} inherited`} />
                </div>
              );
            })()}
          </div>

          {/* ── Accounts ── */}
          <div style={styles.card}>
            <SectionHeader icon="🏦" title="Accounts" subtitle="Owned IRAs feed the Scenario tabs. Inherited IRAs carry their own RMD schedule and never aggregate." />
            {(!client.accounts || !client.accounts.length) ? (
              <Callout type="blue">No accounts yet. Add one below — a prospect needs at least one rough IRA balance for the Scenario tabs.</Callout>
            ) : (
              <>
                <div style={{ overflowX: "auto", marginBottom: 16 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr style={{ background: C.surface }}>
                      {["Account #", "Type", "Balance", "FMV", "Annual Income", "Schedule", ""].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: h === "" ? "center" : "left", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: C.textSec, borderBottom: `1px solid ${C.borderLight}` }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {client.accounts.map((a, i) => (
                        <tr key={a.id} style={{ background: editAccountId === a.id ? "#fffbeb" : i % 2 === 0 ? "#fff" : C.surface }}>
                          <td style={{ padding: "8px 12px", ...styles.mono }}>{a.account_number}</td>
                          <td style={{ padding: "8px 12px" }}>
                            <span style={{ background: a.is_inherited_ira ? "#f0fdf4" : a.is_annuity ? "#eff6ff" : C.surface, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{accountType(a)}</span>
                          </td>
                          <td style={{ padding: "8px 12px", ...styles.mono }}>{usd(a.balance)}</td>
                          <td style={{ padding: "8px 12px", ...styles.mono }}>{a.fair_market_value ? usd(a.fair_market_value) : "—"}</td>
                          <td style={{ padding: "8px 12px", ...styles.mono }}>{a.annual_income ? usd(a.annual_income) : "—"}</td>
                          <td style={{ padding: "8px 12px", fontSize: 11, color: C.textSec }}>{a.is_inherited_ira ? ({ edb: "Stretch", dcb: "10-year", non: "5-yr/ghost" }[a.beneficiary_type || "edb"]) : "Uniform Lifetime"}</td>
                          <td style={{ padding: "8px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                            <button style={{ background: "none", border: "none", cursor: "pointer", color: C.blue, fontSize: 16 }} onClick={() => { setEditAccountId(editAccountId === a.id ? null : a.id); setShowAddAccount(false); }} title="Edit">✏️</button>
                            <button style={{ background: "none", border: "none", cursor: "pointer", color: C.red, fontSize: 16 }} onClick={() => removeAccount(a.id)} title="Remove">✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {editAccountId && client.accounts.find(a => a.id === editAccountId) && (
                  <AccountEditForm account={client.accounts.find(a => a.id === editAccountId)} onSave={updateAccount} onCancel={() => setEditAccountId(null)} />
                )}
              </>
            )}
            {showAddAccount
              ? <AccountForm onAdd={addAccount} onCancel={() => setShowAddAccount(false)} />
              : <button style={styles.btnPrimary} onClick={() => { setShowAddAccount(true); setEditAccountId(null); }}>+ Add Account</button>}
          </div>

          {/* ── Sub-tabs ── */}
          <div style={{ marginBottom: 16 }}>
            <PillTabs
              tabs={[
                { id: "rmd", label: "RMD Calculations" },
                { id: "proj", label: "10-Year Projections" },
                { id: "s204", label: "Scenario Analysis — 204 Income Annuity" },
                { id: "s204fia", label: "Scenario Analysis — 204 with FIA Springing" },
              ]}
              active={subTab} onChange={setSubTab}
            />
          </div>
          {subTab === "rmd" && <RMDTab client={client} logoSrc={logoSrc} />}
          {subTab === "proj" && <ProjectionsTab client={client} logoSrc={logoSrc} />}
          {subTab === "s204" && <ScenarioTab key="s204" client={client} scenario={scenario} setScenario={setScenario} withFIA={false} logoSrc={logoSrc} />}
          {subTab === "s204fia" && <ScenarioTab key="s204fia" client={client} scenario={scenario} setScenario={setScenario} withFIA={true} logoSrc={logoSrc} />}
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
function RMDTab({ client, logoSrc }) {
  const r = useMemo(() => calculateRMD(client), [client]);
  const inhCols = [
    { key: "account_number", label: "Account", align: "left" },
    { key: "balance", label: "Balance", format: usd },
    { key: "divisor", label: "Divisor", format: v => v == null ? "—" : Number(v).toFixed(1) },
    { key: "rmd", label: "RMD", format: usd, color: () => C.red },
    { key: "note", label: "Rule", align: "left" },
  ];
  return (
    <div style={styles.card}>
      <SectionHeader icon="🧮" title="RMD Calculations" subtitle="Current-year required minimum distributions" />
      {r.notRequired ? (
        <Callout type="blue"><strong>Owned-IRA RMD not yet required</strong> — age {r.ageYE}; RMDs begin at age {r.rmdAge} ({r.yearsUntil} yr).</Callout>
      ) : (
        <>
          {r.rmdSatisfied
            ? <HeroBanner color="green" label="✓ Owned-IRA RMD satisfied by annuity income" value={usd(r.annuityIncome)} subtitle={`Required ${usd(r.totalRMD)}`} />
            : <HeroBanner color="navy" label="Additional RMD required (owned IRAs)" value={usd(r.additionalRMD)} subtitle={`Total required ${usd(r.totalRMD)} · annuity income ${usd(r.annuityIncome)}`} />}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Section 204 Aggregate (owned)</div>
              <MetricLine label="Age at Year End" value={r.ageYE} />
              <MetricLine label="Distribution Divisor" value={r.divisor?.toFixed(1)} />
              <MetricLine label="Owned IRA Total" value={usd(r.regularTotal)} />
              <MetricLine label="Annuity FMV Total" value={usd(r.annuityFMVTotal)} />
              <MetricLine label="Total FMV" value={usd(r.totalFMV)} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Distribution Requirements</div>
              <MetricLine label="Total Required RMD" value={usd(r.totalRMD)} />
              <MetricLine label="Annuity Income" value={usd(r.annuityIncome)} color={C.green} />
              <MetricLine label="Additional RMD Needed" value={usd(r.additionalRMD)} color={r.additionalRMD > 0 ? C.red : C.green} />
            </div>
          </div>
        </>
      )}
      {r.inherited.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Inherited IRAs — separate schedules (not aggregable)</div>
          <ProjectionTable rows={r.inherited} columns={inhCols} filename={`${client.last_name}_inherited_rmd.csv`} />
          <MetricLine label="Inherited RMD total" value={usd(r.inheritedTotal)} color={C.red} />
        </div>
      )}
      <div style={{ display: "flex", gap: 12, marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.borderLight}` }}>
        <button style={styles.btnSecondary} onClick={() => downloadEML(client, r, null, 0.05, 0, logoSrc)}>✉️ Email Report</button>
        <button style={styles.btnSecondary} onClick={() => downloadDOCX(client, r, null, 0.05, 0, logoSrc)}>🖨️ Print Report</button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
function ProjectionsTab({ client, logoSrc }) {
  const [growthRate, setGrowthRate] = useState(0.05);
  const [arbitrageRate, setArbitrageRate] = useState(0);
  const [proj, setProj] = useState(null);
  const [view, setView] = useState("standard");
  const rmdResult = useMemo(() => calculateRMD(client), [client]);

  const cols = [
    { key: "Year", label: "Year", align: "left" },
    { key: "AgeYE", label: "Age" },
    { key: "NonAnnuityValue", label: "IRA Value", format: usd },
    { key: "AnnuityFMV", label: "Annuity FMV", format: usd },
    { key: "Divisor", label: "Divisor", format: v => v.toFixed(1) },
    { key: "TotalRMD", label: "Total RMD", format: usd },
    { key: "AnnuityIncome", label: "Annuity Income", format: usd },
    { key: "AdditionalRMD", label: "Additional RMD", format: usd, color: v => v > 0 ? C.red : C.green },
  ];
  const inhCols = [
    { key: "Year", label: "Year", align: "left" },
    { key: "StartValue", label: "Start Value", format: usd },
    { key: "Divisor", label: "Divisor", format: v => v == null ? "—" : Number(v).toFixed(1) },
    { key: "RMD", label: "RMD", format: usd, color: () => C.red },
    { key: "EndValue", label: "End Value", format: usd },
    { key: "Note", label: "Rule", align: "left" },
  ];
  const chartData = proj ? (view === "standard" ? proj.projections : proj.arbProjections).map(r => ({ Year: r.Year, "IRA Value": r.NonAnnuityValue + r.AnnuityFMV, "Total RMD": r.TotalRMD, "Additional RMD": r.AdditionalRMD })) : null;

  return (
    <div style={styles.card}>
      <SectionHeader icon="📈" title="10-Year Projections" subtitle="Owned aggregate, plus each inherited IRA on its own schedule" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <SliderField label="Growth Rate" value={growthRate} onChange={setGrowthRate} min={0} max={0.15} step={0.001} />
        <SliderField label="Arbitrage Rate" value={arbitrageRate} onChange={setArbitrageRate} min={0} max={0.10} step={0.001} />
      </div>
      <button style={styles.btnPrimary} onClick={() => setProj(calculateProjections(client, growthRate, arbitrageRate))}>Calculate Projections</button>
      {proj && (
        <div style={{ marginTop: 24 }}>
          <PillTabs tabs={[{ id: "standard", label: "Standard Projection" }, { id: "arbitrage", label: "Arbitrage Projection" }]} active={view} onChange={setView} />
          <DualAxisChart data={chartData} lines={[
            { key: "IRA Value", name: "IRA Value", color: C.navy, axis: "left" },
            { key: "Total RMD", name: "Total RMD", color: C.red, axis: "right", dashed: true },
            { key: "Additional RMD", name: "Additional RMD", color: C.green, axis: "right", dashed: true },
          ]} />
          <ProjectionTable rows={view === "standard" ? proj.projections : proj.arbProjections} columns={cols} filename={`${client.last_name}_${view}_projection.csv`} />
          {proj.inherited.map(inh => (
            <div key={inh.id} style={{ marginTop: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>Inherited IRA {inh.account_number} — {inh.rule.type === "dcb" ? "10-year rule" : inh.rule.type === "non" ? "5-year / ghost life" : "stretch"}</div>
              <ProjectionTable rows={inh.rows} columns={inhCols} filename={`${client.last_name}_inherited_${inh.account_number}.csv`} />
            </div>
          ))}
          <div style={{ display: "flex", gap: 12, marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.borderLight}` }}>
            <button style={styles.btnSecondary} onClick={() => downloadEML(client, rmdResult, proj, growthRate, arbitrageRate, logoSrc)}>✉️ Email Report</button>
            <button style={styles.btnSecondary} onClick={() => downloadDOCX(client, rmdResult, proj, growthRate, arbitrageRate, logoSrc)}>🖨️ Print Report</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
const INDEX_META = [
  { key: "spx",  label: "SPX",  name: "S&P 500 TR",   color: "#1e3a5f" },
  { key: "djia", label: "DJIA", name: "Dow Jones",    color: "#2563eb" },
  { key: "ndx",  label: "NDX",  name: "Nasdaq-100",   color: "#7c3aed" },
  { key: "eafe", label: "EAFE", name: "MSCI EAFE",    color: "#059669" },
  { key: "agg",  label: "AGG",  name: "Barclays Agg", color: "#d97706" },
];

function ScenarioTab({ client, scenario, setScenario, withFIA, logoSrc }) {
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("spia");
  const [chartView, setChartView] = useState("value");
  const [frontierData, setFrontierData] = useState(null);
  const [frontierMetric, setFrontierMetric] = useState("wealth");

  const S = scenario;
  const set = (k, v) => setScenario({ [k]: v });
  const setFia = (patch) => setScenario({ fia: { ...S.fia, ...patch } });
  const iraAssets = scenarioBalance(client);
  const currentYear = new Date().getFullYear();
  const ageYE = ageAtYearEnd(client.dob);
  const isHist = S.returnMode === "hist";
  // ages → 1-based projection years (rebalance happens at the END of that year)
  const rebalanceYears = String(S.rebalanceAges || "").split(/[,\s]+/).map(Number).filter(a => a > 0)
    .map(a => a - ageYE).filter(n => n >= 1 && n <= 9);
  const fiaCfg = withFIA ? { allocationPct: S.fia.allocationPct, term: S.fia.term, participation: S.fia.participation, cap: S.fia.cap > 0 ? S.fia.cap : null, rebalance: S.fia.rebalance, terminateYear: S.fia.terminateYear } : null;

  const run = () => {
    setResult(runScenario({
      startBalance: iraAssets, ageYE, currentYear,
      returns: isHist
        ? { mode: "hist", allocs: S.allocs, yearBegin: S.yearBegin, yearEnd: S.yearEnd, flipped: S.flipped }
        : { mode: "assumed", equityRate: S.equityRate, bondRate: S.bondRate, bondPct: S.bondPct, dividendYield: S.dividendYield ?? 0.02 },
      spia: { allocationPct: S.allocationPct, term: S.spiaTerm, rate: S.spiaRate, arbitrageRate: S.arbitrageRate, rebalanceYears },
      fia: fiaCfg, horizon: 10,
    }));
  };
  const sum = summarize(result);

  const handleAlloc = (key, raw) => {
    const allocs = { ...S.allocs };
    const newVal = Math.max(0, Math.min(100, Math.round(Number(raw))));
    const others = Object.keys(allocs).filter(k => k !== key);
    const otherTotal = others.reduce((s, k) => s + allocs[k], 0);
    const remaining = 100 - newVal;
    const next = { ...allocs, [key]: newVal };
    if (otherTotal === 0) { const each = Math.floor(remaining / others.length); others.forEach(k => next[k] = each); }
    else others.forEach(k => next[k] = Math.round(allocs[k] * remaining / otherTotal));
    const s = Object.values(next).reduce((a, b) => a + b, 0);
    if (s !== 100) { const fix = others.find(k => next[k] > 0) || others[0]; next[fix] += (100 - s); }
    set("allocs", next);
  };

  const baselineCols = [
    { key: "Year", label: "Year", align: "left" }, { key: "Age", label: "Age" },
    ...(isHist ? [{ key: "RetYear", label: "Ret. Yr" }] : []),
    { key: "Return", label: "Return", format: pct },
    { key: "IRAValue", label: "IRA Value", format: usd },
    { key: "Divisor", label: "Divisor", format: v => v.toFixed(1) },
    { key: "RMD", label: "RMD", format: usd },
  ];
  const spiaCols = [
    { key: "Year", label: "Year", align: "left" }, { key: "Age", label: "Age" },
    ...(isHist ? [{ key: "RetYear", label: "Ret. Yr" }] : []),
    { key: "Return", label: "Mgd Return", format: pct },
    { key: "BondPct", label: "Bonds %", format: v => `${(v * 100).toFixed(0)}%` },
    { key: "IRAValue", label: "IRA Value", format: usd },
    ...(withFIA ? [
      { key: "ManagedValue", label: "Managed", format: usd },
      { key: "FIAValue", label: "FIA Value", format: usd },
      { key: "FIAFMV", label: "FIA FMV (RMD base)", format: usd, color: () => AMBER },
      { key: "FIACredit", label: "Credit", format: v => v == null ? "" : pct(v), color: () => AMBER },
    ] : []),
    { key: "Divisor", label: "Divisor", format: v => v.toFixed(1) },
    { key: "RMD", label: "Gross RMD", format: usd },
    ...((result?.tranches?.length || 0) > 1 ? result.tranches.map(t => ({ key: `T${t.id}Income`, label: `T${t.id} Income`, format: v => v ? usd(v) : "—", color: () => C.green })) : []),
    { key: "SPIAIncome", label: (result?.tranches?.length || 0) > 1 ? "SPIA Income (all)" : "SPIA Income", format: usd, color: () => C.green },
    { key: "SPIAFMV", label: "SPIA FMV", format: usd },
    { key: "SPIAPV", label: "SPIA PV (remaining)", format: usd },
    { key: "AdditionalRMD", label: "Add'l RMD", format: usd },
    { key: "TotalSPIARMD", label: "Total 204 RMD", format: usd, color: () => C.blue },
  ];
  const chartData = result ? result.baseline.map((b, i) => {
    const s = result.spiaProj[i];
    return {
      Year: b.Year,
      "Baseline Value": Math.round(b.IRAValue), "204 Value": Math.round(s.IRAValue),
      "Baseline RMD": Math.round(b.RMD), "204 Total RMD": Math.round(s.TotalSPIARMD),
      "Baseline Total": Math.round(b.IRAValue + b.RMD), "204 Total": Math.round(s.IRAValue + s.TotalSPIARMD),
    };
  }) : null;
  const chartViews = {
    value: { label: "(i) Portfolio Value", lines: [{ key: "Baseline Value", name: "Baseline", color: C.navy, axis: "left" }, { key: "204 Value", name: "Sec. 204", color: C.blue, axis: "left" }] },
    rmd:   { label: "(ii) RMD Obligation", lines: [{ key: "Baseline RMD", name: "Baseline RMD", color: C.red, axis: "right", dashed: true }, { key: "204 Total RMD", name: "204 Total RMD", color: C.green, axis: "right", dashed: true }] },
    total: { label: "(iii) Value + RMD", lines: [{ key: "Baseline Total", name: "Baseline", color: C.navy, axis: "left" }, { key: "204 Total", name: "Sec. 204", color: C.green, axis: "left" }] },
  };
  const anniversaries = Array.from({ length: Math.floor(10 / (S.fia.term || 1)) }, (_, k) => (k + 1) * (S.fia.term || 1));

  return (
    <div>
      {/* ── Return source ── */}
      <div style={styles.card}>
        <SectionHeader icon="📐" title={withFIA ? "Scenario Analysis — Sec. 204 with FIA Springing" : "Scenario Analysis — Sec. 204 Income Annuity"} subtitle="Baseline (no annuity) vs. Sec. 204 on the owned IRA balance" />
        {iraAssets <= 0 && <Callout type="amber">No owned IRA balance — add a Traditional IRA account above.</Callout>}
        <PillTabs tabs={[{ id: "assumed", label: "Assumed Rate" }, { id: "hist", label: "Backtest — Historical Sequence" }]} active={S.returnMode} onChange={v => set("returnMode", v)} />
        {!isHist ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
              <SliderField label="Equity Sleeve RoR" value={S.equityRate} onChange={v => set("equityRate", v)} min={0} max={0.15} step={0.001} />
              <SliderField label="Bond Sleeve RoR" value={S.bondRate} onChange={v => set("bondRate", v)} min={0} max={0.10} step={0.001} />
              <SliderField label="Bond Sleeve (% of IRA)" value={S.bondPct} onChange={v => set("bondPct", v)} min={0} max={1} step={0.01} />
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
              Blended baseline RoR {pct(S.equityRate * (1 - S.bondPct) + S.bondRate * S.bondPct)}. SPIA premiums and the FIA slice are drawn from the bond sleeve first; the remaining mix is never re-bought, so the managed sleeve tilts to equity by exactly what was annuitized.
            </div>
          </>
        ) : (
          <>
            <Callout type="blue">Current age and balance are the anchor; the selected years supply the return sequence only. {withFIA && <>The FIA credits the <strong>S&amp;P 500 Price Index</strong> over each term — dividends excluded.</>}</Callout>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px", marginBottom: 12 }}>
              {INDEX_META.map(({ key, label, name, color }) => (
                <div key={key}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color }}>{label} <span style={{ fontWeight: 400, color: C.textSec }}>— {name}</span></span>
                    <span style={{ fontSize: 12, fontWeight: 700, color }}>{S.allocs[key]}%</span>
                  </div>
                  <input type="range" min={0} max={100} step={1} value={S.allocs[key]} onChange={e => handleAlloc(key, e.target.value)} style={{ width: "100%", accentColor: color, cursor: "pointer" }} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div><label style={styles.label}>Begin Year</label>
                <select style={styles.input} value={S.yearBegin} onChange={e => { const y = Number(e.target.value); set("yearBegin", y); if (S.yearEnd < y + 9) set("yearEnd", Math.min(HIST_LAST, y + 9)); }}>
                  {Array.from({ length: HIST_LAST - 9 - HIST_FIRST + 1 }, (_, k) => HIST_FIRST + k).map(y => <option key={y} value={y}>{y}</option>)}
                </select></div>
              <div style={{ fontSize: 12, color: C.textSec, paddingBottom: 10 }}>→ {S.yearBegin + 9} (10-year window)</div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600, color: C.navy, paddingBottom: 10, cursor: "pointer" }}>
                <input type="checkbox" checked={S.flipped} onChange={e => set("flipped", e.target.checked)} /> Flip sequence
              </label>
            </div>
          </>
        )}
      </div>

      {/* ── SPIA parameters (shared by both scenario tabs) ── */}
      <div style={styles.card}>
        <SectionHeader icon="📊" title="SPIA Parameters" subtitle="Shared by both Scenario tabs — saved on this person's record" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <SliderField label="SPIA Allocation (% of RMD)" value={S.allocationPct} onChange={v => set("allocationPct", v)} min={0} max={1} step={0.01} />
          <SliderField label="SPIA Term (years)" value={S.spiaTerm} onChange={v => set("spiaTerm", v)} min={1} max={10} step={1} fmt={v => `${v} yr`} />
          <SliderField label="Internal SPIA Rate" value={S.spiaRate} onChange={v => set("spiaRate", v)} min={0} max={0.07} step={0.0001} />
          <SliderField label="Arbitrage Rate" value={S.arbitrageRate} onChange={v => set("arbitrageRate", v)} min={0} max={0.10} step={0.001} />
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={styles.label}>Rebalance to plan at ages (comma-separated, optional)</label>
            <input style={styles.input} value={S.rebalanceAges} placeholder={`e.g. ${ageYE + 5}, ${ageYE + 8}`} onChange={e => set("rebalanceAges", e.target.value)} />
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
              At the end of each listed year the allocation rule re-runs: a new SPIA tranche sized to {pct(S.allocationPct)} of the next year's projected RMD less income still paying, same term and rate, premium from Managed{withFIA ? " (FIA credit first on a credit year)" : ""}.
              {rebalanceYears.length > 0 && <> Active: year{rebalanceYears.length > 1 ? "s" : ""} {rebalanceYears.join(", ")}.</>}
            </div>
          </div>
        </div>
        {result && result.mode === "assumed" && result.sleeves.fixedKey && (
          <Callout type="amber">
            Premium {usd(result.premium)} — {usd(result.premiumFromFixed)} from bonds{result.fiaFromFixed > 0 ? `; FIA slice ${usd(result.fia.amount)} — ${usd(result.fiaFromFixed)} from bonds` : ""}.
            Bond sleeve {pct(S.bondPct)} → {pct(result.spiaProj[0]?.BondPct || 0)} after purchase; managed RoR {pct(result.baseRate)} → {pct(result.spiaProj[0]?.Return || 0)}.
          </Callout>
        )}
      </div>

      {/* ── FIA parameters (tab 4 only) ── */}
      {withFIA && (
        <div style={{ ...styles.card, borderLeft: `4px solid ${AMBER}` }}>
          <SectionHeader icon="⚡" title="FIA Springing Parameters" subtitle="A slice of the post-premium IRA in a point-to-point FIA. FMV held at basis between anniversaries; credited on the anniversary, then re-arms." />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <SliderField label="FIA Allocation (% of deferred IRA after SPIA premium)" value={S.fia.allocationPct} onChange={v => setFia({ allocationPct: v })} min={0} max={1} step={0.01} />
            <SliderField label="Springing Term (years)" value={S.fia.term} onChange={v => setFia({ term: v, terminateYear: 0 })} min={1} max={10} step={1} fmt={v => `${v} yr`} />
            <div><label style={styles.label}>Index</label><input style={{ ...styles.input, background: C.surface }} value={isHist ? "S&P 500 Price Index (historical)" : `Equity RoR less ${pct(S.dividendYield ?? 0.02)} dividend yield (price-index proxy)`} readOnly /></div>
            {!isHist && <SliderField label="Assumed Dividend Yield (equity − yield = price index)" value={S.dividendYield ?? 0.02} onChange={v => set("dividendYield", v)} min={0} max={0.05} step={0.001} />}
            <SliderField label="Participation Rate" value={S.fia.participation} onChange={v => setFia({ participation: v })} min={0} max={2} step={0.01} fmt={v => `${(v * 100).toFixed(0)}%`} />
            <SliderField label="Term Cap (0 = uncapped)" value={S.fia.cap} onChange={v => setFia({ cap: v })} min={0} max={3} step={0.01} fmt={v => v > 0 ? `${(v * 100).toFixed(0)}%` : "Uncapped"} />
            <div>
              <label style={styles.label}>Terminate FIA (credits to Managed)</label>
              <select style={styles.input} value={S.fia.terminateYear} onChange={e => setFia({ terminateYear: Number(e.target.value) })}>
                <option value={0}>Never — runs through horizon</option>
                {anniversaries.map(y => <option key={y} value={y}>Year {y} (anniversary)</option>)}
              </select>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 12, fontWeight: 600, color: C.navy }}>
              <input type="checkbox" checked={S.fia.rebalance} onChange={e => setFia({ rebalance: e.target.checked })} /> Maintain allocation — rebalance to FIA share at each credit
            </label>
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 10 }}>
            Rules: 0% floor per term on basis net of withdrawals · RMD shortfall drawn Managed → FIA · SPIA premiums never sourced from the FIA · rebalance and termination only at a credit.
          </div>
        </div>
      )}

      {/* ── Run ── */}
      <div style={styles.card}>
        {result && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 16 }}>
            <StatCard label="Owned IRA (year-end)" value={usd(result.projectedValue)} />
            <StatCard label="Projected RMD" value={usd(result.rmd)} />
            <StatCard label="SPIA Premium" value={usd(result.premium)} accent sublabel={`${pct(S.allocationPct)} of RMD`} />
            {withFIA && result.fia && <StatCard label="FIA Premium" value={usd(result.fia.amount)} sublabel={`${pct(result.fia.allocationPct)} of deferred · ${result.fia.term}-yr`} />}
          </div>
        )}
        <button style={styles.btnPrimary} onClick={run} disabled={iraAssets <= 0}>Run Scenario</button>
      </div>

      {result && sum && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <HeroBanner color="navy" label="10-Yr Value Delta (204 − Baseline)" value={usd(sum.endDelta)} subtitle="IRA value at year 10" />
            <HeroBanner color={sum.rmdDelta <= 0 ? "green" : "indigo"} label="Cumulative RMD Delta" value={usd(sum.rmdDelta)} subtitle="204 total RMD vs baseline (10-yr sum)" />
            <HeroBanner color="navy" label="Total Wealth Delta" value={usd(sum.wealthDelta)} subtitle={`Value + PV of remaining SPIA payments (${usd(sum.spiaPVEnd)}) + cumulative RMD`} />
          </div>
          {result.tranches.length > 1 && (
            <div style={styles.card}>
              <SectionHeader icon="🧩" title="SPIA Tranches" subtitle="Each tranche amortizes on its own schedule; PV of remaining payments is carried in total wealth" />
              <ProjectionTable rows={result.tranches.map(t => {
                const rb = result.spiaProj.find(p => p.Rebalance && p.Rebalance.year === t.startIdx);
                return { Tranche: `T${t.id}`, Purchased: t.startIdx === 0 ? "Year 1 (start)" : `End of year ${t.startIdx} (age ${ageYE + t.startIdx})`, Premium: t.premium, AnnualIncome: t.annualIncome, Term: `${t.term} yr`, Pays: `Yr ${t.startIdx + 1}–${t.endIdx + 1}`, Source: t.startIdx === 0 ? "IRA" : rb ? `Managed ${usd(rb.Rebalance.fromManaged)}${rb.Rebalance.fromFIA > 0 ? ` · FIA ${usd(rb.Rebalance.fromFIA)}` : ""}` : "—" };
              })} columns={[
                { key: "Tranche", label: "Tranche", align: "left" }, { key: "Purchased", label: "Purchased", align: "left" },
                { key: "Premium", label: "Premium", format: usd }, { key: "AnnualIncome", label: "Annual Income", format: usd },
                { key: "Term", label: "Term" }, { key: "Pays", label: "Pays", align: "left" }, { key: "Source", label: "Source", align: "left" },
              ]} filename={`${client.last_name}_spia_tranches.csv`} />
            </div>
          )}
          <div style={styles.card}>
            <SectionHeader icon="📉" title="Comparison" />
            <PillTabs tabs={Object.entries(chartViews).map(([id, v]) => ({ id, label: v.label }))} active={chartView} onChange={setChartView} />
            <DualAxisChart data={chartData} lines={chartViews[chartView].lines} />
          </div>
          <div style={styles.card}>
            <PillTabs tabs={[{ id: "baseline", label: "Baseline" }, { id: "spia", label: withFIA ? "Sec. 204 + FIA" : "Sec. 204" }]} active={activeTab} onChange={setActiveTab} />
            {activeTab === "baseline"
              ? <ProjectionTable rows={result.baseline} columns={baselineCols} filename={`${client.last_name}_baseline.csv`} />
              : <ProjectionTable rows={result.spiaProj} columns={spiaCols} filename={`${client.last_name}_204${withFIA ? "_fia" : ""}.csv`} />}
            <div style={{ display: "flex", gap: 12, marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.borderLight}`, alignItems: "center", flexWrap: "wrap" }}>
              <button style={styles.btnSecondary} onClick={() => downloadProspectEML(client, result, iraAssets, result.baseRate, result.spiaProj[0]?.Return || result.baseRate, S.allocationPct, S.spiaTerm, S.spiaRate, S.arbitrageRate, frontierData, frontierMetric, logoSrc)}>✉️ Email Report</button>
              <button style={styles.btnSecondary} onClick={() => downloadProspectDOCX(client, result, iraAssets, result.baseRate, result.spiaProj[0]?.Return || result.baseRate, S.allocationPct, S.spiaTerm, S.spiaRate, S.arbitrageRate, frontierData, frontierMetric, logoSrc)}>🖨️ Print Report</button>
              {!frontierData && !isHist && <span style={{ fontSize: 12, color: C.textMuted }}>Run the Efficient Frontier to include the scatter map</span>}
            </div>
          </div>
        </>
      )}

      {/* ── Frontier (assumed-rate mode) ── */}
      {!isHist && iraAssets > 0 && (
        <EfficientFrontierChart
          client={client} iraAssets={iraAssets} equityRate={S.equityRate} bondRate={S.bondRate}
          spiaTerm={S.spiaTerm} spiaRate={S.spiaRate} arbitrageRate={S.arbitrageRate}
          fia={fiaCfg} rebalanceYears={rebalanceYears}
          onFrontierReady={(data, metric) => { setFrontierData(data); setFrontierMetric(metric); }}
        />
      )}
      {isHist && <Callout type="blue">The Efficient Frontier sweeps bond-sleeve sizes at assumed rates; switch to Assumed Rate to run it.</Callout>}
    </div>
  );
}
